import React, { Suspense, lazy } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useEditableTranscript } from '../features/transcript-editor/hooks/useEditableTranscript';
import { streamingVideoUrl } from '../features/transcript-editor/utils/streamingVideoUrl';

// Slate, Virtuoso and the editor's own code load only when an editor opens,
// not with the rest of the app (~250-400 KB less initial JS).
const TranscriptEditor = lazy(() => import('../features/transcript-editor/components/TranscriptEditor'));

const TITLE_STYLE = { color: '#18181b', fontSize: 18, fontWeight: 600, marginBottom: 8 };
const TEXT_STYLE = { color: '#71717a', fontSize: 13.5, marginBottom: 16 };

const LoadingScreen = () => (
  <div className="te-page-state">
    <div className="text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300 border-t-gray-700 mx-auto mb-4"></div>
      <p style={{ color: '#71717a', fontSize: 13 }}>Loading session...</p>
    </div>
  </div>
);

const MessageScreen = ({ title, message, actionLabel, onAction }) => (
  <div className="te-page-state">
    <div className="text-center max-w-md" style={{ padding: 24 }}>
      <h1 style={TITLE_STYLE}>{title}</h1>
      <p style={{ ...TEXT_STYLE, whiteSpace: 'pre-line' }}>{message}</p>
      <button onClick={onAction} className="te-btn te-btn-secondary">
        {actionLabel}
      </button>
    </div>
  </div>
);

const EditorFallback = () => (
  <div style={{ padding: 32, textAlign: 'center', color: '#71717a' }}>
    Loading editor…
  </div>
);

export default function TranscriptEditPage() {
  const { id: sessionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Read only: switching the language inside the editor is disabled.
  const lang = searchParams.get('lang') || 'en';
  const { session, blocks, loading, error } = useEditableTranscript(sessionId, lang);

  if (loading) return <LoadingScreen />;
  if (error) {
    return (
      <MessageScreen
        title="Cannot edit transcript"
        message={error}
        actionLabel="Back to sessions"
        onAction={() => navigate('/sessions')}
      />
    );
  }
  if (blocks.length === 0) {
    return (
      <MessageScreen
        title="No transcript"
        message="This session doesn't have a transcript yet. Please generate one first."
        actionLabel="Go to session details"
        onAction={() => navigate(`/sessions/${sessionId}`)}
      />
    );
  }

  return (
    <Suspense fallback={<EditorFallback />}>
      <TranscriptEditor
        key={lang}
        sessionId={sessionId}
        videoUrl={streamingVideoUrl(session)}
        initialBlocks={blocks}
        sessionTitle={session?.title}
        backUrl={`/sessions/${sessionId}`}
      />
    </Suspense>
  );
}
